import React, { useContext, useState } from 'react';
import { INav, useCreateNavMutation, useUpdateNavMutation } from '../../../../generated/graphql';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import { parseDate } from '../../../../utils/clientUtils';
import DialogBox from '../../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import { Colors } from '../../../../utils/theme';
import InputComponent from '../../../Common/Fields/InputComponent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';

interface IProps {
  stockId: string;
  handleClose: (saved: boolean, isNew?: boolean, navInput?: INav) => void;
  navInput: INav | null;
}

type IErrors = {
  date: string | null;
  dateStart: string | null;
};

const defaultErrorsObj: IErrors = {
  date: null,
  dateStart: null
};

const NavForm: React.FunctionComponent<IProps> = ({ stockId, navInput, handleClose }: IProps) => {
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [dateCreated, setDateCreated] = useState<Date | null>(
    navInput ? parseDate(navInput.created_date) : new Date()
  );
  const [dateStart, setDateStart] = useState<Date | null>(
    navInput ? parseDate(navInput.start_date) : null
  );
  const [navElement, setNavElement] = useState<INav | null>(navInput);
  const [createNav, createNavResult] = useCreateNavMutation();
  const [updateNav, updateNavResult] = useUpdateNavMutation();
  const { dispatch, state } = useContext(AppContext);
  const [editable, setEditable] = useState(navInput === null);
  const canEdit = canUserEdit(state.user, Types.Stock);

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, date: null });
      }
    } else {
      setFormErrors({ ...formErrors, date: 'Date is required!' });
    }
    setDateCreated(input);
  };

  const handleDateStartChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, dateStart: null });
      }
    } else {
      setFormErrors({ ...formErrors, dateStart: 'Date Start is required!' });
    }
    setDateStart(input);
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createNav({
        variables: {
          stock_id: stockId,
          input: {
            ...navElement,
            start_date: (dateStart as Date).toString(),
            created_date: (dateCreated as Date).toString()
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as INav);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Nav created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      const { id, average, nav, nav_epra, nav_epra_history, ndv, nra, nta, ta, tl } =
        navElement as INav;
      updateNav({
        variables: {
          id,
          input: {
            average,
            nav,
            nav_epra,
            nav_epra_history,
            ndv,
            nra,
            nta,
            ta,
            tl,
            start_date: (dateStart as Date).toString(),
            created_date: (dateCreated as Date).toString()
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as INav);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Nav updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      date: null,
      dateStart: null
    };

    if (!dateCreated) {
      hasErrors = true;
      errors.date = 'Date is required!';
    }

    if (!dateStart) {
      hasErrors = true;
      errors.dateStart = 'Date Start is required!';
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      if (navInput) {
        handleSave('update');
      } else {
        handleSave('create');
      }
    }
  };

  const handleCancel = () => {
    if (editable && navInput !== null) {
      setNavElement(navInput);
      setDateCreated(parseDate(navInput.created_date));
      setDateStart(parseDate(navInput.start_date));
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  return (
    <DialogBox
      id="nav-dialog"
      width={690}
      title={navInput ? 'NAV Details' : 'Create NAV'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {navInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="nav-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box display="flex" gap="40px" flexDirection="column" sx={{ my: '20px' }}>
        <Box display="flex" gap="24px">
          <InputComponent
            type="number"
            id="total-assets"
            label="Total Assets"
            value={navElement?.ta || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                ta: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
          <InputComponent
            id="total-liabilities"
            label="Total Liabilities"
            type="number"
            value={navElement?.tl || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                tl: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
        </Box>
        <Box display="flex" gap="24px">
          <InputComponent
            id="nav"
            type="number"
            label="NAV"
            value={navElement?.nav || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                nav: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
          <InputComponent
            id="nav-epra"
            label="NAV Epra"
            type="number"
            value={navElement?.nav_epra || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                nav_epra: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
          <InputComponent
            id="nav-epra-history"
            label="NAV Epra History"
            type="number"
            value={navElement?.nav_epra_history || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                nav_epra_history:
                  e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
        </Box>
        <Box display="flex" gap="24px">
          <InputComponent
            id="nra"
            label="NRA"
            type="number"
            value={navElement?.nra || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                nra: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
          <InputComponent
            id="ndv"
            label="NDV"
            type="number"
            value={navElement?.ndv || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                ndv: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
          <InputComponent
            id="nta"
            label="NTA"
            type="number"
            value={navElement?.nta || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                nta: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
        </Box>
        <Box display="flex" gap="24px">
          <InputComponent
            id="average"
            label="Average"
            type="number"
            value={navElement?.average || ''}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onChange={(e) =>
              setNavElement({
                ...navElement,
                average: e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
              } as INav)
            }
          />
          <Box sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="date-created-picker"
                label="Date"
                value={dateCreated}
                onChange={handleDateChange}
                errorMsg={formErrors.date}
                disabled={createNavResult.loading || updateNavResult.loading || !editable}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="date-start-picker"
                label="Date Start"
                value={dateStart}
                onChange={handleDateStartChange}
                errorMsg={formErrors.dateStart}
                disabled={createNavResult.loading || updateNavResult.loading || !editable}
              />
            </LocalizationProvider>
          </Box>
        </Box>
        <Box
          className="centralize"
          sx={{
            gridColumn: 'span 3',
            marginTop: '50px',
            paddingBottom: '45px'
          }}
        >
          <CancelButton
            id="cancel-new-contact-button"
            disabled={createNavResult.loading || updateNavResult.loading}
            onClick={handleCancel}
            text={navInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="create-new-contact-button"
            data-testid="create-new-contact-button"
            text={navInput === null ? 'Create' : 'Save'}
            disabled={createNavResult.loading || updateNavResult.loading || !editable}
            onClick={handleSubmit}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default NavForm;
